/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import qs from 'qs';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    string, bool, func, node, object,
} from 'prop-types';
import mbpLogger from '@module-logger/mbp';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import { MenuList } from '@material-ui/core';
import { flagOnQasFailed } from '../../../../../../state/ducks/Common/ducks/AddressSearchQAS/AddressSearchQAS-Actions';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getFeatureFlags } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { findLoqateAddress, retrieveLoqateAddress } from '../../../../../../apis/loqate-address-apis/findLoqateAddress';
import normalizeLoqateFindResponse from '../../../../../components/GraphqlComponents/GraphqlCategoryPage/Partials/GraphqlLeftMenuContainer/Partials/GraphqlGuidedNav/Loqate';
import LoqateSuggestionList from '../../../../../components/GraphqlComponents/GraphqlCategoryPage/Partials/GraphqlLeftMenuContainer/Partials/LoqateSuggestionList';

const styles = {
    wrapSuggestion: {
        position: 'relative',
    },
    autoSuggestion: {
        position: 'absolute',
        backgroundColor: '#FFF',
        zIndex: 3000,
        width: '100%',
        padding: '0px 3px',
    },
    addressList: {
        fontSize: '12px',
        padding: '4px',
        fontWeight: '600',
        borderBottom: 'solid 1px #ecdede',
    },
    menuListWrapper: {
        padding: '0px',
    },
};

const AddressVerificationSearch = ({
    searchText,
    searchId,
    children,
    isFocus,
    countryCode,
    useGeoLocation,
    onBlurHandler,
    getPreFillAddressDetails,
    track,
    zipCode,
    state,
    isValidZipCode,
    searchTextFocus,
}) => {
    const [autoCompleteResult, setAutoCompleteResult] = useState({});
    const [showLoqateModal, setShowLoqateModal] = useState(false);
    const [previousSuggestions, setPreviousSuggestions] = useState([]);
    const delayAutoComplete = useRef(null);
    const searchContainerRef = useRef(null);
    const countryISOAlfa3Code = {
        US: 'USA',
        CA: 'CAN',
    };

    const getGeoLocation = () => new Promise((resolve, reject) => {
        if (typeof window === 'undefined') {
            reject(new Error('noWindow'));
        } else if (navigator && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(resolve, reject);
        } else {
            reject(new Error('noSupport'));
        }
    });
    const location = useRef({
        lat: '',
        long: '',
    });

    const initGeoLocation = useRef(false);

    const getUserLocation = useCallback(async () => {
        try {
            const data = await getGeoLocation();
            const { coords } = data;

            if (coords) {
                location.current = {
                    lat: `${coords.latitude}`,
                    long: `${coords.longitude}`,
                };
            }
        } catch (response) {
            mbpLogger.logDebug({
                function: '',
                appName: process.env.npm_package_name,
                module: 'getGeoLocation - AddressVerificationSearch',
                message: 'User has rejected get the geolocation',
                response,
            });

            location.current = {
                lat: '',
                long: '',
            };
        }
    }, []);

    const handleNoMatch = useCallback(() => {
        onBlurHandler();
    }, [onBlurHandler]);

    const goBackToPreviousList = () => {
        setPreviousSuggestions((prev) => {
            if (!prev.length) {
                return prev;
            }
            const stack = [...prev];
            const previous = stack.pop();
            setAutoCompleteResult(previous);
            return stack;
        });
    };

    const returnSelectedAddress = useCallback(
        (formattedAddress) => {
            const address1 = `${formattedAddress?.BuildingNumber || ''} ${formattedAddress?.Street || ''}`.trim();
            const address2 = formattedAddress?.SubBuilding || '';
            const selectedAddress = {
                addressLine1: address1,
                addressLine2: address2,
                city: formattedAddress?.City,
                state: formattedAddress?.ProvinceCode,
                country:
                    formattedAddress?.CountryIso2
                    || formattedAddress?.CountryIso3
                    || '',
                zipcode: formattedAddress?.PostalCode?.split('-')[0],
            };
            getPreFillAddressDetails(selectedAddress);
            setShowLoqateModal(false);
            return selectedAddress;
        },
        [getPreFillAddressDetails],
    );

    const handlePickAnAddress = useCallback(
        async (address) => {
            try {
                if (address.isContainer) {
                    const response = await findLoqateAddress({
                        Text: address.text || address.suggestion,
                        Countries: countryCode,
                        Limit: 100,
                        IsMiddleware: true,
                        Container: address.id,
                    });
                    const data = normalizeLoqateFindResponse(response.data);
                    setPreviousSuggestions((prev) => [
                        ...(prev || []),
                        autoCompleteResult,
                    ]);
                    setAutoCompleteResult({
                        count: data.count,
                        results: data.results,
                    });
                    return;
                }
                const response = await retrieveLoqateAddress({
                    Id: address.id,
                });
                const data = normalizeLoqateFindResponse(response.data);
                const raw = data?.results?.[0]?.raw;
                if (!raw) {
                    onBlurHandler();
                    setShowLoqateModal(false);
                    return;
                }
                const addressData = returnSelectedAddress(raw);
                track({
                    eventCategory: 'Checkout',
                    eventAction: 'Selected Loqate Address',
                    eventLabel: `${addressData.addressLine1 || ''}, ${
                        addressData.city || ''
                    } ${addressData.state || ''} ${
                        addressData.zipcode || ''
                    }`,
                });
            } catch (error) {
                console.error(error);
                onBlurHandler();
                setShowLoqateModal(false);
            }
        },
        [
            autoCompleteResult,
            onBlurHandler,
            returnSelectedAddress,
            track,
        ],
    );

    const clearSuggestions = useCallback(() => {
        setAutoCompleteResult({});
        setPreviousSuggestions([]);
        setShowLoqateModal(false);
    }, []);

    const searchAddress = useCallback(async (text) => {
        let addressSearchText = text || '';

        if (addressSearchText.length < 3) return;
        const isoCountryCode = countryISOAlfa3Code[countryCode];

        if (isValidZipCode && zipCode && zipCode !== '00000') {
            const searchZipCode = zipCode.split('-')[0];
            let stateZipCode = '';
            if (state) stateZipCode = (`${stateZipCode} ${state}`).trim();
            if (searchZipCode) stateZipCode = (`${stateZipCode} ${searchZipCode}`).trim();
            addressSearchText = `${addressSearchText.trim()}, ${stateZipCode}`;
        }

        if (isoCountryCode) {
            try {
                const response = await findLoqateAddress({
                    Text: addressSearchText.trim(),
                    Countries: countryCode,
                    Limit: 100,
                    IsMiddleware: true,
                });
                const res = normalizeLoqateFindResponse(response.data);
                setShowLoqateModal(true);
                setAutoCompleteResult({
                    count: res.count,
                    results: res.results,
                });
            } catch (error) {
                console.error(error);
            }
        }
    }, [countryCode, isValidZipCode, zipCode, state]);

    const handleClickAway = () => {
        clearSuggestions();
        onBlurHandler();
    };

    const handleSuggestionListClickAway = (event) => {
        if (searchContainerRef.current?.contains(event.target)) return;
        handleClickAway();
    };

    const handleAutoCompleteBlur = useCallback(() => {
        if (typeof window === 'undefined') return;
        if (delayAutoComplete.current) {
            clearTimeout(delayAutoComplete.current);
        }
        delayAutoComplete.current = setTimeout(() => {
            onBlurHandler();
        }, 700);
    }, [onBlurHandler]);

    useEffect(() => {
        if (!isFocus) {
            searchAddress(searchText);
        }
    }, [searchText, isFocus, searchAddress]);

    useEffect(() => {
        if (searchTextFocus && searchText === '') {
            clearSuggestions();
        }
    }, [searchTextFocus, searchText, clearSuggestions]);

    useEffect(() => {
        if (
            useGeoLocation
            && (!initGeoLocation.current || countryCode)
        ) {
            initGeoLocation.current = true;
            getUserLocation();
        }
    }, [countryCode, useGeoLocation, getUserLocation]);

    useEffect(() => () => {
        if (delayAutoComplete.current) {
            clearTimeout(delayAutoComplete.current);
        }
    }, []);

    return (
        <div
            id={`auto_${searchId}`}
            ref={searchContainerRef}
            onBlur={handleAutoCompleteBlur}
        >
            {children}
            <LoqateSuggestionList
                goBackToPreviousList={goBackToPreviousList}
                suggestions={autoCompleteResult}
                showModel={showLoqateModal}
                previousSuggestions={previousSuggestions}
                handlePickAnAddress={handlePickAnAddress}
                handleClickAway={handleSuggestionListClickAway}
            />
        </div>
    );
};

AddressVerificationSearch.defaultProps = {
    onBlurHandler: () => {},
    useGeoLocation: false,
    isFocus: false,
    zipCode: '',
    isValidZipCode: false,
    state: '',
    countryCode: 'US',
    track: () => {},
    searchTextFocus: false,
};

AddressVerificationSearch.propTypes = {
    getPreFillAddressDetails: func.isRequired,
    searchText: string.isRequired,
    searchId: string.isRequired,
    children: node.isRequired,
    onBlurHandler: func,
    useGeoLocation: bool,
    isFocus: bool,
    zipCode: string,
    isValidZipCode: bool,
    state: string,
    countryCode: string,
    track: func,
    searchTextFocus: bool,
};

const mapDispatchToProps = (dispatch) => ({
    callFlagOnQasFailed: bindActionCreators(flagOnQasFailed, dispatch),
    track: bindActionCreators(trackEvent, dispatch),
});
const mapStateToProps = (state) => ({
    featureFlags: getFeatureFlags(state),
});
export default connect(mapStateToProps, mapDispatchToProps)(AddressVerificationSearch);
